import { useCallback, useState } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import * as storage from "@/lib/storage";
import { checkOutpatientHealth } from "@/services/base";
import SessionService from "@/services/session.service";
import sessionSocketService from "@/services/session-socket.service";
import * as LocalStorage from "@/storages/LocalStorage";
import { useUserStore } from "@/stores";
import { getUserId, useAdditionalInfo } from "@/stores/domains/additionalInfo";
import { resetAllStores } from "@/stores/utils/resetAllStores";
import { clearPersistedStores, Logger } from "@/utils";
import { fingerprintUserId, matchStoredUser } from "@/utils/userIdentityFingerprint";
import {
  REDIRECT_PATH_KEY,
  REQUIRE_ADDITIONAL_INFO_KEY,
  SSO_AUTH_PROGRESS_KEY,
} from "../constants";
import { saveAuthReturnPath } from "../redirect";
import { initializeHealthcareContext } from "../services/healthcareService";
import type { User } from "../types";

// Module-level lock to prevent concurrent session registration
let sessionRegistrationPromise: Promise<void> | null = null;

const hasSelectedAdditionalInfo = () => {
  const state = useAdditionalInfo.getState();
  return Boolean(state.organization && state.location);
};

interface UseAuthStateReturn {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  performLogoutCleanup: () => void;
  handlePostLoginInitialization: () => Promise<void>;
  restoreUserSession: () => Promise<boolean>;
  saveUserSession: (userData: User) => Promise<void>;
}

export const useAuthState = (): UseAuthStateReturn => {
  const oidcAuth = useOidcAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { setProfile, updateProfile } = useUserStore();

  const saveUserSession = useCallback(
    async (userData: User) => {
      // Invariant (ECHO-2318): the session user and React state must be written in the same tick as the
      // access token, so no `await` may precede them. AuthContext.isAuthenticated is derived at render
      // time, and the route guards clear the SSO-in-progress flag as soon as they observe an
      // unauthenticated, non-loading state; deferring setUser behind an await causes a login redirect loop.
      // Store user in sessionStorage for tab isolation
      sessionStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      const currentProfile = useUserStore.getState().profile;
      if (currentProfile) {
        updateProfile({
          id: userData.id,
          username: userData.username,
          name: userData.name,
          email: userData.email,
          avatar: userData.avatar,
        });
        Logger.info("[Auth] Updated existing profile with basic user data");
      } else {
        setProfile(userData);
        Logger.info("[Auth] Created new profile with basic user data");
      }

      const newUserId = userData.id || userData.username;
      // The additional-info store only keeps a fingerprint of the owner (ECHO-2318).
      const newFingerprint = newUserId ? await fingerprintUserId(newUserId) : undefined;
      const storedUserId = getUserId();
      const storedUserMatch = matchStoredUser(storedUserId, newUserId, newFingerprint);

      Logger.info("[Auth] saveUserSession called", {
        hasStoredUserId: !!storedUserId,
        storedUserMatch,
      });

      if (storedUserMatch === "different") {
        Logger.info("[Auth] User changed - clearing additional info");
        useAdditionalInfo.getState().reset();
      } else if (storedUserMatch === "legacy-same" && newFingerprint) {
        // Same user, but an older build stored the plaintext id: replace it in place.
        useAdditionalInfo.getState().setUserId(newFingerprint);
      } else if (storedUserMatch === "missing") {
        const additionalInfoState = useAdditionalInfo.getState();
        const hasAdditionalInfo = additionalInfoState.organization || additionalInfoState.location;
        if (hasAdditionalInfo) {
          Logger.info("[Auth] Found legacy additional info without userId - clearing");
          useAdditionalInfo.getState().reset();
        }
      }
    },
    [setProfile, updateProfile],
  );

  const performLogoutCleanup = useCallback(() => {
    saveAuthReturnPath();

    oidcAuth.removeUser();
    LocalStorage.clearTokens();
    LocalStorage.clearSession();
    LocalStorage.removeItem("session_initializing");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem(REQUIRE_ADDITIONAL_INFO_KEY);
    storage.safeRemoveMultiple(["id_token", storage.CODE_VERIFIER_KEY, REDIRECT_PATH_KEY]);
    // The PKCE verifier now lives in sessionStorage; the line above purges any legacy copy.
    storage.sessionStore.safeRemove(storage.CODE_VERIFIER_KEY);
    sessionStorage.removeItem(SSO_AUTH_PROGRESS_KEY);

    // Clear OIDC library internal state from sessionStorage
    // This prevents stale OIDC state from interfering with new login attempts
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("oidc.")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    sessionSocketService.clearSession();
    sessionSocketService.disconnect();

    setUser(null);
    clearPersistedStores();
    resetAllStores();

    Logger.info("Logout cleanup completed");
  }, [oidcAuth]);

  const handlePostLoginInitialization = useCallback(async () => {
    // If registration is already in progress, wait for it to complete
    if (sessionRegistrationPromise) {
      Logger.info("Session registration already in progress, waiting...");
      await sessionRegistrationPromise;
      return;
    }

    // Set initialization flag FIRST to block other API requests
    // This ensures /session/register is called before any other API requests
    LocalStorage.setItem("session_initializing", "true");

    // Clear old session data before registering new session
    // This ensures we get fresh session after each TPass login
    const existingSessionId = LocalStorage.getSessionId();
    if (existingSessionId) {
      Logger.info("Clearing existing session before new registration", {
        oldSessionId: existingSessionId,
      });
      sessionSocketService.clearSession();
      sessionSocketService.disconnect();
      LocalStorage.clearSession();
    }

    // Create a promise to lock concurrent calls
    sessionRegistrationPromise = (async () => {
      try {
        // Step 1: Register session API (get session ID from server)
        Logger.info("Registering session...");
        const sessionData = await SessionService.registerSession();

        // Store session ID in localStorage
        LocalStorage.setSessionId(sessionData.sessionId);
        Logger.info("Session registered successfully", { sessionId: sessionData.sessionId });

        // Step 2: Health check
        await checkOutpatientHealth();
        Logger.info("Token health check passed");

        Logger.info("Session registered; socket registration deferred until AdditionalInfo");
      } finally {
        // Clear the lock and initialization flag
        sessionRegistrationPromise = null;
        LocalStorage.removeItem("session_initializing");
      }
    })();

    await sessionRegistrationPromise;
  }, []);

  const restoreUserSession = useCallback(async (): Promise<boolean> => {
    const token = LocalStorage.getAccessToken();
    const storedUserStr = sessionStorage.getItem("user");
    let storedUser: User | null = null;

    if (storedUserStr) {
      try {
        storedUser = JSON.parse(storedUserStr);
      } catch (error) {
        Logger.error("Stored user payload is invalid JSON:", error);
        setUser(null);
        sessionStorage.removeItem("user");
        LocalStorage.clearTokens();
        LocalStorage.clearSession();
        LocalStorage.removeItem("session_initializing");
        sessionSocketService.clearSession();
        sessionSocketService.disconnect();
        clearPersistedStores();
        resetAllStores();
        return false;
      }
    }

    if (!token) {
      Logger.info("[New Tab] No token - clearing all auth data");
      setUser(null);
      sessionStorage.removeItem("user");
      LocalStorage.clearTokens();
      LocalStorage.clearSession();
      sessionSocketService.clearSession();
      sessionSocketService.disconnect();
      return false;
    }

    if (!storedUser) {
      setUser(null);
      sessionStorage.removeItem("user");
      LocalStorage.clearTokens();
      LocalStorage.clearSession();
      sessionSocketService.clearSession();
      sessionSocketService.disconnect();
      return false;
    }

    try {
      // Step 1: Health check FIRST to validate/refresh token if needed
      await checkOutpatientHealth();

      // Step 2: Handle session - wait for ongoing registration, refresh if exists, or register new
      // If registration is in progress (from handlePostLoginInitialization), wait for it
      if (sessionRegistrationPromise) {
        Logger.info("Session registration in progress, waiting...");
        await sessionRegistrationPromise;
      }

      const existingSessionId = LocalStorage.getSessionId();
      let currentSessionId = existingSessionId;

      if (existingSessionId) {
        // Session exists, try to refresh it to ensure it's still valid on server
        try {
          await SessionService.refreshSession();
          Logger.info("Session refreshed on page refresh", { sessionId: existingSessionId });
        } catch (refreshError) {
          // Session invalid on server, need to re-register
          Logger.warn("Session refresh failed, re-registering", refreshError);
          LocalStorage.clearSession();

          // Use lock to register new session
          if (!sessionRegistrationPromise) {
            // Set initialization flag to block other API requests
            LocalStorage.setItem("session_initializing", "true");
            sessionRegistrationPromise = (async () => {
              try {
                const sessionData = await SessionService.registerSession();
                LocalStorage.setSessionId(sessionData.sessionId);
                currentSessionId = sessionData.sessionId;
                Logger.info("Session re-registered after refresh failure", {
                  sessionId: sessionData.sessionId,
                });
              } finally {
                sessionRegistrationPromise = null;
                LocalStorage.removeItem("session_initializing");
              }
            })();
            await sessionRegistrationPromise;
          } else {
            await sessionRegistrationPromise;
            currentSessionId = LocalStorage.getSessionId();
          }
        }
      } else {
        // No session, use lock to register new session
        if (!sessionRegistrationPromise) {
          Logger.info("Registering new session on page refresh");
          // Set initialization flag to block other API requests
          LocalStorage.setItem("session_initializing", "true");
          sessionRegistrationPromise = (async () => {
            try {
              const sessionData = await SessionService.registerSession();
              LocalStorage.setSessionId(sessionData.sessionId);
              currentSessionId = sessionData.sessionId;
              Logger.info("Session registered on page refresh", {
                sessionId: sessionData.sessionId,
              });
            } finally {
              sessionRegistrationPromise = null;
              LocalStorage.removeItem("session_initializing");
            }
          })();
          await sessionRegistrationPromise;
        } else {
          Logger.info("Session registration in progress, waiting...");
          await sessionRegistrationPromise;
          currentSessionId = LocalStorage.getSessionId();
        }
      }

      await saveUserSession(storedUser);

      const hasAdditionalInfo = hasSelectedAdditionalInfo();

      // Step 3: Initialize healthcare context only after AdditionalInfo exists
      if (hasAdditionalInfo) {
        try {
          await initializeHealthcareContext();
          Logger.info("Healthcare context restored after page refresh");
        } catch (contextError) {
          Logger.warn("Failed to restore healthcare context:", contextError);
        }
      }

      // Step 4: Connect socket only after AdditionalInfo has selected organization/clinic
      if (currentSessionId && hasAdditionalInfo) {
        if (!sessionSocketService.isSocketConnected()) {
          sessionSocketService.connect();
        }
        sessionSocketService.registerSession(currentSessionId);
        Logger.info("Session socket connected and registered after page refresh");
      }

      return true;
    } catch (error) {
      // Clear initialization flag on error
      LocalStorage.removeItem("session_initializing");
      Logger.error("Session restoration failed:", error);
      return false;
    }
  }, [saveUserSession]);

  return {
    user,
    loading,
    setUser,
    setLoading,
    performLogoutCleanup,
    handlePostLoginInitialization,
    restoreUserSession,
    saveUserSession,
  };
};
